import Decimal from 'decimal.js';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { getCargoOperationForCargo, getLastBerthCallInPortCall, isBefore, isBeforeOrEqual } from '../stowageRotationUtils';
import { isShipFigureAdjusted, stowedWeightInTanks } from './stowageOperationUtils';
import { getTankContentInBerthCall } from 'src/utils/stowage/manifest/cargoManifestUtils';
import { OperationType } from 'src/constants/stowage/operationTypes';

/**
 * Check if a stowage item has an adjusted ship figure that should be used.
 * For loading operations, requires isShipFigureAdjusted=true.
 * For unloading operations, the presence of shipFigure is sufficient.
 *
 * @param item - The stowage item to check
 * @param requireAdjustedFlag - Whether to require isShipFigureAdjusted=true (default: true, for loading)
 * @returns true if the item has a ship figure that should be used
 */
const shouldUseAdjustedShipFigure = (item: Stowage, requireAdjustedFlag: boolean = true): boolean => {
    const hasShipFigure = item.shipFigure !== null && item.shipFigure !== undefined;
    return hasShipFigure && (!requireAdjustedFlag || item.isShipFigureAdjusted === true);
};

/**
 * Check if this is the last unloading operation for the cargo.
 * The "last" unloading is the final discharge port chronologically.
 *
 * @param stowagePlan - The stowage plan containing cargo operations
 * @param unloadingOperation - The unloading operation to check
 * @returns true if this is the last unloading operation for the cargo
 */
export const isLastUnloadingOperation = (stowagePlan: StowagePlan, unloadingOperation: RotationCargoOperation): boolean => {
    return checkIfLastUnloadingOperation(stowagePlan, unloadingOperation);
};

/**
 * Get the ROB (Remaining On Board) remainder for the last unloading operation for a specific tank.
 *
 * Priority 1: Use ship figure from previous operation (if available) - represents ROB
 * Priority 2: Calculate: sumLoaded - sumPreviouslyUnloaded
 *
 * @param stowagePlan - The stowage plan containing stowage items
 * @param lastUnloadingOperation - The last unloading operation
 * @param tankId - The tank ID to get the ROB for
 * @returns Decimal - The ROB remainder for this tank
 */
export const getLastUnloadingROB = (stowagePlan: StowagePlan, lastUnloadingOperation: RotationCargoOperation, tankId: number): Decimal => {
    // Find the tank from the stowage items
    const stowageItemForTank = stowagePlan.stowageItems.find(
        (item) => item.cargoOperationGuid === lastUnloadingOperation.cargoOperationGuid && item.tank?.tankId === tankId
    );

    if (!stowageItemForTank?.tank) {
        return new Decimal(0);
    }

    // Use the same calculation as calculateLastUnloadingShipFigureForTank
    return calculateLastUnloadingShipFigureForTank(stowagePlan, lastUnloadingOperation, stowageItemForTank.tank);
};

/**
 * Check if a loading operation occurred before the transit port call (when there's no berth call)
 * Uses the port call sequence in the rotation to determine order
 */
const isLoadingBeforeTransitPortCall = (stowagePlan: StowagePlan, loadingOp: RotationCargoOperation, transitOp: RotationCargoOperation): boolean => {
    // Find the index of the loading operation's port call in the rotation
    const loadingPortCallIndex = stowagePlan.rotation.findIndex((p) => p.portCallGuid === loadingOp.portCallGuid);
    // Find the index of the transit operation's port call in the rotation
    const transitPortCallIndex = stowagePlan.rotation.findIndex((p) => p.portCallGuid === transitOp.portCallGuid);

    // Loading should be included only if it's strictly before the transit port call
    // (Loading at the same port call hasn't happened yet from the transit's perspective)
    return loadingPortCallIndex !== -1 && transitPortCallIndex !== -1 && loadingPortCallIndex < transitPortCallIndex;
};

/**
 * Check if an unloading operation occurred before the transit port call (when there's no berth call)
 * Uses the port call sequence in the rotation to determine order
 */
const isUnloadingBeforeTransitPortCall = (
    stowagePlan: StowagePlan,
    unloadingOp: RotationCargoOperation,
    transitOp: RotationCargoOperation
): boolean => {
    // Find the index of the unloading operation's port call in the rotation
    const unloadingPortCallIndex = stowagePlan.rotation.findIndex((p) => p.portCallGuid === unloadingOp.portCallGuid);
    // Find the index of the transit operation's port call in the rotation
    const transitPortCallIndex = stowagePlan.rotation.findIndex((p) => p.portCallGuid === transitOp.portCallGuid);

    // At port-call granularity, transit reflects arrival minus unloading in the current port call.
    // Include unloading at the same port call when berth-level ordering is unavailable.
    return unloadingPortCallIndex !== -1 && transitPortCallIndex !== -1 && unloadingPortCallIndex <= transitPortCallIndex;
};

/**
 * Sort cargo operations by their chronological voyage order using berth call sequence.
 * cargoOperationSequenceNumber represents unloading priority at destination, not voyage order.
 *
 * For operations without berth calls (like Transit), uses port call order from rotation.
 *
 * @param stowagePlan - The stowage plan containing berth calls and rotation
 * @param operations - Array of cargo operations to sort
 * @returns Sorted array of cargo operations in chronological voyage order
 */
const sortCargoOperationsByChronologicalOrder = (stowagePlan: StowagePlan, operations: RotationCargoOperation[]): RotationCargoOperation[] => {
    return [...operations].sort((a, b) => {
        // If both have berth calls, use isBefore for comparison
        if (a.berthCall && b.berthCall) {
            return isBefore(stowagePlan.berthCalls, a.berthCall, b.berthCall) ? -1 : 1;
        }

        // If one or both don't have berth calls, use port call order from rotation
        const aPortCallIndex = stowagePlan.rotation.findIndex((p) => p.portCallGuid === a.portCallGuid);
        const bPortCallIndex = stowagePlan.rotation.findIndex((p) => p.portCallGuid === b.portCallGuid);

        // If both found in rotation, compare by index
        if (aPortCallIndex !== -1 && bPortCallIndex !== -1) {
            if (aPortCallIndex !== bPortCallIndex) {
                return aPortCallIndex - bPortCallIndex;
            }
            // Same port call - Loading comes before Unloading which comes before Transit
            const operationOrder: Record<string, number> = {
                [OperationType.LOADING]: 0,
                [OperationType.UNLOADING]: 1,
                [OperationType.TRANSIT]: 2,
            };
            return (operationOrder[a.operationType] ?? 3) - (operationOrder[b.operationType] ?? 3);
        }

        // Fallback: operations not found in rotation go to end
        if (aPortCallIndex === -1 && bPortCallIndex === -1) return 0;
        if (aPortCallIndex === -1) return 1;
        return -1;
    });
};

export const getShipFigureCssClass = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): string => {
    if (isShipFigureAdjusted(stowagePlan, cargoOperation)) {
        return 'adjusted-ship-figure';
    } else {
        return '';
    }
};

/**
 * Calculate ship figure for a cargo operation based on operation type
 * @param stowagePlan - The stowage plan containing stowage items
 * @param cargoOperation - The cargo operation to calculate ship figure for
 * @returns Decimal - The aggregated ship figure for the operation
 */
export const getShipFigureForOperationType = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): Decimal => {
    // Collect all related stowage items for the cargo operation
    const relatedStowageItems = stowagePlan.stowageItems.filter((item) => item.cargoOperationGuid === cargoOperation.cargoOperationGuid);

    // For transit operations we do not have direct stowage items, so don't early-return.
    if (relatedStowageItems.length === 0 && cargoOperation.operationType !== OperationType.TRANSIT) {
        return new Decimal(0);
    }

    if (cargoOperation.operationType === OperationType.LOADING) {
        return calculateLoadingOperationShipFigure(stowagePlan, cargoOperation, relatedStowageItems);
    } else if (cargoOperation.operationType === OperationType.UNLOADING) {
        return calculateUnloadingOperationShipFigure(stowagePlan, cargoOperation, relatedStowageItems);
    } else if (cargoOperation.operationType === OperationType.TRANSIT) {
        // We need to calculate current ship figure by summing up loading operations, then subtracting unloading operations.

        // Get all operations for this cargo
        const allCargoOps = getCargoOperationForCargo(stowagePlan, cargoOperation.cargo);

        const portCall = stowagePlan.rotation.find((p) => p.portCallGuid === cargoOperation.portCallGuid);
        const currentBerthCall = getLastBerthCallInPortCall(portCall);

        // Calculate total loaded weight (ship figure) from loading operations that occurred BEFORE this transit
        // Loading at the same port call as the transit is excluded — transit represents cargo from previous ports only.
        // When a port has multiple berths, loading at an earlier berth is NOT included in transit
        // because that loading already has its own row in the operation list.
        let totalLoaded = new Decimal(0);
        allCargoOps
            .filter((op) => op.operationType === OperationType.LOADING)
            .forEach((op) => {
                // Always use port-call-level comparison to exclude same-port loading.
                // Loading at earlier berths within the same port call should NOT be included.
                const isSamePortCall = op.portCallGuid === cargoOperation.portCallGuid;
                if (isSamePortCall) return;

                const shouldInclude = currentBerthCall
                    ? isBefore(stowagePlan.berthCalls, op.berthCall, currentBerthCall)
                    : isLoadingBeforeTransitPortCall(stowagePlan, op, cargoOperation);

                if (shouldInclude) {
                    const items = stowagePlan.stowageItems.filter((i) => i.cargoOperationGuid === op.cargoOperationGuid);
                    totalLoaded = totalLoaded.plus(calculateLoadingOperationShipFigure(stowagePlan, op, items));
                }
            });

        // Calculate total unloaded weight from unloading operations that occurred up to this transit
        // For transit calculations, we need the actual weight unloaded, not the remaining ship figure
        let totalUnloaded = new Decimal(0);
        allCargoOps
            .filter((op) => op.operationType === OperationType.UNLOADING)
            .forEach((op) => {
                const shouldInclude = currentBerthCall
                    ? isBeforeOrEqual(stowagePlan.berthCalls, op.berthCall, currentBerthCall)
                    : isUnloadingBeforeTransitPortCall(stowagePlan, op, cargoOperation);

                if (shouldInclude) {
                    // For transit calculations, sum the actual weight unloaded
                    // IMPORTANT: shipFigure on unloading items represents ROB (Remaining On Board),
                    // NOT the amount unloaded. We must calculate: actualUnloaded = previousROB - shipFigure
                    const items = stowagePlan.stowageItems.filter((i) => i.cargoOperationGuid === op.cargoOperationGuid);
                    let unloadedWeight = new Decimal(0);
                    items.forEach((item) => {
                        // Use getActualUnloadedShipFigure to correctly calculate unloaded amount
                        // This handles both cases: shipFigure set (ROB) and shipFigure null (use weight)
                        if (isLastUnloadingForTank(stowagePlan, op, item.tank?.tankId || 0)) {
                            // For last unloading of this tank, use last unloading ROB calculation
                            const lastUnloadingROB = getLastUnloadingROB(stowagePlan, op, item.tank?.tankId || 0);
                            unloadedWeight = unloadedWeight.plus(lastUnloadingROB);
                        } else {
                            const actualUnloaded = getActualUnloadedShipFigure(stowagePlan, op, item);
                            unloadedWeight = unloadedWeight.plus(actualUnloaded);
                        }
                    });
                    totalUnloaded = totalUnloaded.plus(unloadedWeight);
                }
            });

        // Transit ship figure = Total loaded - Total unloaded (up to this point)
        const transitFigure = totalLoaded.minus(totalUnloaded);

        // Ensure we don't return negative ship figures
        return transitFigure.gt(0) ? transitFigure : new Decimal(0);
    }

    // TODO: Implement for other operation types (Transfer, etc.)
    return new Decimal(0);
};

/**
 * Check if the current unloading operation is the LAST unloading for a SPECIFIC TANK.
 *
 * Unlike checkIfLastUnloadingOperation which checks at the operation level,
 * this function determines per-tank whether any subsequent unloading operations
 * touch this specific tank.
 *
 * @param stowagePlan - The stowage plan containing all operations
 * @param currentUnloadingOperation - The current unloading operation
 * @param tankId - The specific tank to check
 * @returns true if no future unloading operations touch this tank
 */
export const isLastUnloadingForTank = (stowagePlan: StowagePlan, currentUnloadingOperation: RotationCargoOperation, tankId: number): boolean => {
    // Get all unloading operations for this cargo, sorted chronologically
    const unloadingOperations = sortCargoOperationsByChronologicalOrder(
        stowagePlan,
        getCargoOperationForCargo(stowagePlan, currentUnloadingOperation.cargo).filter((op) => op.operationType === OperationType.UNLOADING)
    );

    // Find current operation's index
    const currentIndex = unloadingOperations.findIndex((op) => op.cargoOperationGuid === currentUnloadingOperation.cargoOperationGuid);

    // Look at all subsequent unloading operations
    for (let i = currentIndex + 1; i < unloadingOperations.length; i++) {
        const futureOperation = unloadingOperations[i];

        // Check if future operation has a stowage item for this tank
        const hasTankInFutureOp = stowagePlan.stowageItems.some(
            (item) => item.cargoOperationGuid === futureOperation.cargoOperationGuid && item.tank?.tankId === tankId
        );

        if (hasTankInFutureOp) {
            // This tank will be unloaded again in a future operation
            return false;
        }
    }

    // No future operations touch this tank - this is the last unloading
    return true;
};

const calculateLoadingOperationShipFigure = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation, stowageItems: Stowage[]): Decimal => {
    let aggregatedShipFigure = new Decimal(0);

    stowageItems.forEach((item) => {
        // Use ship figure if it's not null and isShipFigureAdjusted is true
        if (shouldUseAdjustedShipFigure(item, true)) {
            // shipFigure on a loading stowage item represents the total ROB in the tank after loading.
            // If this tank already had cargo from a previous operation (e.g., loaded at an earlier port),
            // we must subtract the previous ROB to get only the amount loaded at THIS port.
            const previousROB = getPreviousOperationROB(stowagePlan, cargoOperation, item.tank);
            const actualLoaded = new Decimal(item.shipFigure!).minus(previousROB);
            const loadedAmount = actualLoaded.gt(0) ? actualLoaded : new Decimal(0);

            aggregatedShipFigure = aggregatedShipFigure.plus(loadedAmount);
        } else {
            // Otherwise use the weight property (represents the amount loaded at this port)
            if (item.weight) {
                aggregatedShipFigure = aggregatedShipFigure.plus(item.weight);
            }
        }
    });

    return aggregatedShipFigure;
};

const calculateUnloadingOperationShipFigure = (
    stowagePlan: StowagePlan,
    cargoOperation: RotationCargoOperation,
    stowageItems: Stowage[]
): Decimal => {
    // Calculate ship figure per-tank to handle split unloading scenarios
    // where some tanks are fully discharged (final) while others are partially discharged
    let aggregatedShipFigure = new Decimal(0);

    stowageItems.forEach((stowageItem) => {
        if (!stowageItem.tank) {
            return;
        }

        const tankId = stowageItem.tank.tankId;

        // Per-tank check: Is THIS tank in its final unloading?
        const isLastForThisTank = isLastUnloadingForTank(stowagePlan, cargoOperation, tankId);

        let tankShipFigure: Decimal;

        if (isLastForThisTank) {
            // This tank is being fully discharged
            // Use "last unloading" calculation: totalLoaded - totalPreviouslyUnloaded
            tankShipFigure = calculateLastUnloadingShipFigureForTank(stowagePlan, cargoOperation, stowageItem.tank);
        } else {
            // This tank is being partially discharged
            // Use "regular unloading" calculation: previousROB - currentROB

            if (stowageItem.shipFigure !== null && stowageItem.shipFigure !== undefined) {
                // Ship figure is set - calculate based on previous operation
                tankShipFigure = calculateUnloadingShipFigureForTank(stowagePlan, cargoOperation, stowageItem);
            } else {
                // Ship figure not set - use weight property
                tankShipFigure = stowageItem.weight ? stowageItem.weight.abs() : new Decimal(0);
            }
        }

        aggregatedShipFigure = aggregatedShipFigure.plus(tankShipFigure);
    });

    return aggregatedShipFigure;
};

const calculateLastUnloadingShipFigureForTank = (
    stowagePlan: StowagePlan,
    currentUnloadingOperation: RotationCargoOperation,
    tank: Tank
): Decimal => {
    // Get all operations for the same cargo, sorted by chronological voyage order
    const cargoOperations = sortCargoOperationsByChronologicalOrder(
        stowagePlan,
        getCargoOperationForCargo(stowagePlan, currentUnloadingOperation.cargo)
    );

    // Calculate total loaded into this tank
    let totalLoaded = new Decimal(0);

    // Calculate total unloaded from this tank (excluding current operation)
    let totalPreviouslyUnloaded = new Decimal(0);

    cargoOperations.forEach((operation) => {
        const stowageItemsForTank = stowagePlan.stowageItems.filter(
            (item) => item.cargoOperationGuid === operation.cargoOperationGuid && item.tank?.tankId === tank.tankId
        );

        stowageItemsForTank.forEach((item) => {
            if (operation.operationType === OperationType.LOADING) {
                // Aggregate loading operations
                // IMPORTANT: shipFigure represents total ROB in tank, not the amount loaded.
                // Must subtract previousROB to get actual loaded amount (same logic as calculateLoadingOperationShipFigure).
                let loadingShipFigure: Decimal;
                if (shouldUseAdjustedShipFigure(item, true)) {
                    const previousROB = getPreviousOperationROB(stowagePlan, operation, item.tank);
                    const actualLoaded = new Decimal(item.shipFigure!).minus(previousROB);
                    loadingShipFigure = actualLoaded.gt(0) ? actualLoaded : new Decimal(0);
                } else {
                    loadingShipFigure = item.weight ? item.weight.abs() : new Decimal(0);
                }

                totalLoaded = totalLoaded.plus(loadingShipFigure);
            } else if (
                operation.operationType === OperationType.UNLOADING &&
                operation.cargoOperationGuid !== currentUnloadingOperation.cargoOperationGuid
            ) {
                // Aggregate previous unloading operations (exclude current operation)
                // IMPORTANT: shipFigure represents ROB after unloading, NOT the amount unloaded
                // We use getActualUnloadedShipFigure to correctly calculate the unloaded amount
                const unloadingShipFigure = getActualUnloadedShipFigure(stowagePlan, operation, item);

                totalPreviouslyUnloaded = totalPreviouslyUnloaded.plus(unloadingShipFigure);
            }
        });
    });

    // Final calculation: Total loaded - Total previously unloaded
    const remainingShipFigure = totalLoaded.minus(totalPreviouslyUnloaded);

    // Ensure we don't return negative ship figures
    return remainingShipFigure.gt(0) ? remainingShipFigure : new Decimal(0);
};

/**
 * Get the ROB (Remaining On Board) from the previous operation for a specific tank.
 * This is the ship figure value from the previous operation's stowage item.
 *
 * NOTE: Transit operations don't have stowage items, so we need to skip back through
 * the operation chain until we find a Loading or Unloading operation with a stowage
 * item for this tank.
 *
 * IMPORTANT: Uses chronological voyage order (berth call sequence) NOT cargoOperationSequenceNumber
 * which represents unloading priority at destination.
 */
const getPreviousOperationROB = (stowagePlan: StowagePlan, currentOperation: RotationCargoOperation, tank: Tank | null | undefined): Decimal => {
    // Get all operations for this cargo, sorted by CHRONOLOGICAL voyage order (NOT sequence number)
    // This ensures we find the operation that actually preceded this one in the voyage,
    // not the one with the lower sequence number (which is unloading priority)
    const cargoOperations = sortCargoOperationsByChronologicalOrder(stowagePlan, getCargoOperationForCargo(stowagePlan, currentOperation.cargo));
    // Find the current operation by GUID AND operationType to handle cases where Transit and Unloading have the same GUID
    const currentIndex = cargoOperations.findIndex(
        (op) => op.cargoOperationGuid === currentOperation.cargoOperationGuid && op.operationType === currentOperation.operationType
    );

    // Walk backwards through operations to find one with a stowage item for this tank
    for (let i = currentIndex - 1; i >= 0; i--) {
        const previousOperation = cargoOperations[i];

        // Skip Transit operations - they don't have stowage items
        if (previousOperation.operationType === OperationType.TRANSIT) {
            continue;
        }

        const previousStowageItem = stowagePlan.stowageItems.find(
            (item) => item.cargoOperationGuid === previousOperation.cargoOperationGuid && item.tank?.tankId === tank?.tankId
        );

        if (previousStowageItem) {
            // For loading operations, only use shipFigure if isShipFigureAdjusted is true
            // This aligns with the loading ship figure calculation logic in calculateLoadingOperationShipFigure
            const isLoading = previousOperation.operationType === OperationType.LOADING;
            // Loading requires isShipFigureAdjusted=true; Unloading uses shipFigure regardless of flag
            const useShipFigure = shouldUseAdjustedShipFigure(previousStowageItem, isLoading);

            const rob = useShipFigure
                ? new Decimal(previousStowageItem.shipFigure!)
                : previousStowageItem.weight
                ? previousStowageItem.weight.abs()
                : new Decimal(0);

            return rob;
        }
    }

    return new Decimal(0);
};

/**
 * Calculate the actual unloaded ship figure for a tank.
 *
 * When shipFigure is manually set on an unloading stowage item, it represents the
 * ROB (Remaining On Board) after unloading, NOT the amount unloaded.
 *
 * The actual unloaded amount = previousROB - currentROB (shipFigure)
 *
 * When shipFigure is null/undefined, fall back to |weight| (allocated unloading amount).
 *
 * @param stowagePlan - The stowage plan containing stowage items
 * @param unloadingOperation - The unloading operation
 * @param unloadingItem - The stowage item (tank) being unloaded
 * @returns Decimal - The actual unloaded ship figure (not ROB)
 */
export const getActualUnloadedShipFigure = (
    stowagePlan: StowagePlan,
    unloadingOperation: RotationCargoOperation,
    unloadingItem: Stowage
): Decimal => {
    // If no ship figure set, use weight (current behavior - correct)
    if (unloadingItem.shipFigure === null || unloadingItem.shipFigure === undefined) {
        return unloadingItem.weight ? unloadingItem.weight.abs() : new Decimal(0);
    }

    // Ship figure is set - it represents ROB after unloading
    // Calculate actual unloaded amount: previousROB - currentROB (shipFigure)
    const previousROB = getPreviousOperationROB(stowagePlan, unloadingOperation, unloadingItem.tank);
    const currentROB = new Decimal(unloadingItem.shipFigure);
    const actualUnloadedAmount = previousROB.minus(currentROB);

    // Ensure we don't return negative values
    return actualUnloadedAmount.gt(0) ? actualUnloadedAmount : new Decimal(0);
};

const calculateUnloadingShipFigureForTank = (
    stowagePlan: StowagePlan,
    unloadingOperation: RotationCargoOperation,
    unloadingItem: Stowage
): Decimal => {
    // Use the helper to get the actual unloaded ship figure
    // This correctly handles the case where shipFigure is ROB, not unloaded amount
    return getActualUnloadedShipFigure(stowagePlan, unloadingOperation, unloadingItem);
};

const checkIfLastUnloadingOperation = (stowagePlan: StowagePlan, unloadingOperation: RotationCargoOperation): boolean => {
    // Get all unloading operations for the same cargo, sorted by chronological voyage order
    const unloadingOperations = sortCargoOperationsByChronologicalOrder(
        stowagePlan,
        getCargoOperationForCargo(stowagePlan, unloadingOperation.cargo).filter((op) => op.operationType === OperationType.UNLOADING)
    );

    // Check if this operation is the last in the sorted list
    const lastOperation = unloadingOperations[unloadingOperations.length - 1];
    const isLast = lastOperation?.cargoOperationGuid === unloadingOperation.cargoOperationGuid;

    return isLast;
};

/**
 * Update the hasAdjustedShipFigure function to handle transit operations
 */
export const hasAdjustedShipFigure = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): boolean => {
    const relatedStowageItems = stowagePlan.stowageItems.filter((item) => item.cargoOperationGuid === cargoOperation.cargoOperationGuid);

    if (cargoOperation.operationType === OperationType.LOADING) {
        return relatedStowageItems.some((item) => shouldUseAdjustedShipFigure(item, true));
    } else if (cargoOperation.operationType === OperationType.UNLOADING) {
        // For unloading, consider it adjusted if any ship figure is set (regardless of isShipFigureAdjusted flag)
        return relatedStowageItems.some((item) => shouldUseAdjustedShipFigure(item, false));
    } else if (cargoOperation.operationType === OperationType.TRANSIT) {
        // For transit operations, check if tank content has ship figures set
        const berthCall = cargoOperation.berthCall;
        if (!berthCall) {
            return false;
        }

        return relatedStowageItems.some((stowageItem) => {
            if (!stowageItem.tank) {
                return false;
            }

            const tankContent = getTankContentInBerthCall(stowagePlan, berthCall, stowageItem.tank);
            if (tankContent) {
                const cargoContent = tankContent.cargoContents.find((content) => content.cargo?.cargoGuid === cargoOperation.cargoGuid);

                return cargoContent?.shipFigure !== null && cargoContent?.shipFigure !== undefined;
            }

            return false;
        });
    }

    return false;
};

export const calculateUnloadingDifference = (stowagePlan: StowagePlan, unloadingCargoOperation: RotationCargoOperation): Decimal => {
    if (unloadingCargoOperation.operationType !== OperationType.UNLOADING) {
        console.warn(`calculateUnloadingDifference called with non-unloading operation: ${unloadingCargoOperation.operationType}`);
        return new Decimal(0);
    }

    // Get all operations for the same cargo
    const allCargoOperations = getCargoOperationForCargo(stowagePlan, unloadingCargoOperation.cargo);

    // Separate loading and unloading operations
    const loadingOperations = allCargoOperations.filter((operation) => operation.operationType === OperationType.LOADING);
    const unloadingOperations = allCargoOperations.filter((operation) => operation.operationType === OperationType.UNLOADING);

    // Calculate total loaded weight across all loading operations
    let totalLoadedWeight = new Decimal(0);
    loadingOperations.forEach((loadingOperation) => {
        const loadedWeight = stowedWeightInTanks(stowagePlan, loadingOperation);
        totalLoadedWeight = totalLoadedWeight.plus(loadedWeight);
    });

    // Calculate total unloaded weight across all unloading operations
    let totalUnloadedWeight = new Decimal(0);
    unloadingOperations.forEach((unloadingOperation) => {
        const unloadedWeight = stowedWeightInTanks(stowagePlan, unloadingOperation).abs(); // Use abs() since unloading weights are typically negative
        totalUnloadedWeight = totalUnloadedWeight.plus(unloadedWeight);
    });

    // Calculate difference: total loaded - total unloaded
    const difference = totalLoadedWeight.minus(totalUnloadedWeight);
    return difference;
};
